import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, MagnifyingGlass, Copy, ClipboardText, Notebook, LinkSimple, Code,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LandingPageTheme from "@/components/LandingPageTheme";

const emptyForm = {
  name: "", campaign_id: "", is_active: true,
  theme: null,
  field_config: {
    email_shown: true, email_required: true,
    phone_shown: false, phone_required: false,
    company_shown: false, company_required: false,
  },
};

export default function Forms() {
  const { currentOrg } = useAuth();
  const [forms, setForms] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, c] = await Promise.all([
        api.get("/forms"),
        api.get("/campaigns").catch(() => ({ data: [] })),
      ]);
      setForms(f.data); setCampaigns(c.data || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Forms isn't enabled for this workspace");
      else toast.error("Could not load forms");
      setForms([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const filtered = useMemo(() => {
    if (!query.trim()) return forms;
    const q = query.toLowerCase();
    return forms.filter(x => (x.name || "").toLowerCase().includes(q));
  }, [forms, query]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (f) => {
    setEditing(f.form_id);
    setForm({
      name: f.name || "",
      campaign_id: f.campaign_id || "",
      is_active: !!f.is_active,
      theme: f.theme || null,
      field_config: { ...emptyForm.field_config, ...(f.field_config || {}) },
    });
    setDialogOpen(true);
  };

  const setFC = (k, v) => setForm(prev => ({ ...prev, field_config: { ...prev.field_config, [k]: v } }));

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name, is_active: form.is_active,
        campaign_id: form.campaign_id || (editing ? "" : null),
        field_config: form.field_config,
        // Theme: null (or empty object) means "keep defaults". Server treats
        // an all-blank payload as null so the form renders untouched defaults.
        theme: form.theme || {},
      };
      if (!editing && !payload.campaign_id) delete payload.campaign_id;
      if (editing) {
        const { data } = await api.patch(`/forms/${editing}`, payload);
        setForms(all => all.map(x => x.form_id === editing ? data : x));
        toast.success("Form updated");
      } else {
        const { data } = await api.post("/forms", payload);
        setForms(all => [data, ...all]);
        toast.success("Form created");
      }
      setDialogOpen(false); setForm(emptyForm); setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/forms/${confirmDelete.form_id}`);
      setForms(all => all.filter(x => x.form_id !== confirmDelete.form_id));
      toast.success(`Removed "${confirmDelete.name}"`);
    } catch { toast.error("Could not remove form"); }
    finally { setConfirmDelete(null); }
  };

  const publicUrlFor = (slug) =>
    `${window.location.origin}/f/${slug}`;

  const embedSnippetFor = (slug) =>
    `<iframe src="${publicUrlFor(slug)}" width="480" height="640" frameborder="0" style="border:0;max-width:100%;" title="Form"></iframe>`;

  const copyUrl = async (slug) => {
    try {
      await navigator.clipboard.writeText(publicUrlFor(slug));
      toast.success("Public URL copied");
    } catch { toast.error("Copy failed — select the URL manually"); }
  };

  const copyEmbed = async (slug) => {
    try {
      await navigator.clipboard.writeText(embedSnippetFor(slug));
      toast.success("Embed snippet copied — paste into any site's HTML");
    } catch { toast.error("Copy failed — select the snippet manually"); }
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="forms-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">Workspace</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Forms</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-xl">
            Public forms your prospects can fill in without an account — each submission creates a Lead
            in this workspace automatically. Share the public URL directly today; embed and landing-page
            options are coming next.
          </p>
        </div>
        <button data-testid="forms-add" onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200">
          <Plus weight="bold" className="h-4 w-4" /> New form
        </button>
      </div>

      <div className="mt-6 relative max-w-md">
        <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
        <input data-testid="forms-search" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search by name"
          className="w-full bg-graphite-800 border border-white/10 rounded-md pl-10 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30" />
      </div>

      <div className="mt-6 rounded-md border border-white/10 bg-graphite-900 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-ink-secondary">Loading forms…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="forms-empty">
            <Notebook weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/50" />
            <div className="mt-4 font-display text-xl tracking-tight">
              {forms.length === 0 ? "No forms yet" : "No matches"}
            </div>
            <p className="mt-2 text-sm text-ink-secondary max-w-sm mx-auto">
              {forms.length === 0 ? "Create your first public form to start capturing Leads." : "Try a different search term."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="forms-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Campaign</th>
                  <th className="px-5 py-3 font-medium">Public URL</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map(f => (
                  <tr key={f.form_id} data-testid={`form-row-${f.form_id}`} className="hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-ink-primary" data-testid={`form-name-${f.form_id}`}>{f.name}</td>
                    <td className="px-5 py-3 text-ink-secondary text-xs">{f.campaign_name || "—"}</td>
                    <td className="px-5 py-3">
                      <div className="inline-flex items-center gap-2 text-xs font-mono text-ink-secondary max-w-[280px]">
                        <LinkSimple weight="bold" className="h-3 w-3 shrink-0" />
                        <span data-testid={`form-slug-${f.form_id}`} className="truncate">/f/{f.public_slug}</span>
                        <button data-testid={`form-copy-${f.form_id}`} onClick={() => copyUrl(f.public_slug)}
                          className="text-ink-secondary hover:text-ink-primary" title="Copy full URL">
                          <Copy weight="bold" className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {f.is_active ? (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-emerald-400/30 bg-emerald-400/5 text-emerald-200">Active</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 text-ink-secondary/70">Inactive</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button data-testid={`form-edit-${f.form_id}`} onClick={() => openEdit(f)}
                          className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5"><PencilSimple weight="bold" className="h-3.5 w-3.5" /></button>
                        <button data-testid={`form-delete-${f.form_id}`} onClick={() => setConfirmDelete(f)}
                          className="p-2 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"><Trash weight="bold" className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) { setDialogOpen(v); if (!v) { setForm(emptyForm); setEditing(null); } } }}>
        <DialogContent data-testid="form-dialog" className="max-w-xl bg-graphite-900 border-white/10 text-ink-primary max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">{editing ? "Edit form" : "New form"}</DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Pick what to ask visitors for. Name is always required.
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="content" data-testid="form-edit-tabs">
            <TabsList className="grid grid-cols-2 bg-graphite-950 border border-white/10">
              <TabsTrigger value="content" data-testid="form-tab-content">Content</TabsTrigger>
              <TabsTrigger value="theme"   data-testid="form-tab-theme">Theme</TabsTrigger>
            </TabsList>
            <TabsContent value="content" className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Internal name</label>
              <Input data-testid="form-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} maxLength={200} placeholder="e.g. Newsletter Signup" className="bg-graphite-950 border-white/10" />
              <p className="text-[11px] text-ink-secondary/60">Only your team sees this. Also becomes the lead's source label.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Campaign (optional)</label>
              <select data-testid="form-form-campaign" value={form.campaign_id}
                onChange={e => setForm({ ...form, campaign_id: e.target.value })}
                className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                <option value="">— None —</option>
                {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
              </select>
              <p className="text-[11px] text-ink-secondary/60">Leads from this form will be linked to this campaign.</p>
            </div>

            <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4">
              <div className="text-[11px] uppercase tracking-[0.24em] text-ink-secondary mb-3">Fields</div>
              <div className="space-y-3">
                <div className="flex items-center justify-between opacity-80">
                  <div>
                    <div className="text-sm text-ink-primary">Name</div>
                    <div className="text-[11px] text-ink-secondary">Always shown & required</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-ink-secondary/70">Locked</span>
                </div>
                {["email", "phone", "company"].map(k => (
                  <div key={k} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-ink-primary capitalize">{k}</div>
                      <div className="text-[11px] text-ink-secondary">
                        {form.field_config[`${k}_shown`] ? (form.field_config[`${k}_required`] ? "Shown · required" : "Shown · optional") : "Hidden"}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-ink-secondary">Show</span>
                        <Switch data-testid={`form-field-${k}-shown`}
                          checked={form.field_config[`${k}_shown`]}
                          onCheckedChange={v => setFC(`${k}_shown`, v)} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[11px] ${form.field_config[`${k}_shown`] ? "text-ink-secondary" : "text-ink-secondary/40"}`}>Require</span>
                        <Switch data-testid={`form-field-${k}-required`}
                          checked={form.field_config[`${k}_required`]}
                          disabled={!form.field_config[`${k}_shown`]}
                          onCheckedChange={v => setFC(`${k}_required`, v)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border border-white/10 bg-graphite-950/40 p-4">
              <div>
                <div className="text-sm text-ink-primary">Active</div>
                <div className="text-[11px] text-ink-secondary">Inactive forms return "Not found" on the public URL.</div>
              </div>
              <Switch data-testid="form-form-active" checked={form.is_active}
                onCheckedChange={v => setForm({ ...form, is_active: v })} />
            </div>

            {editing && (
              <div className="rounded-md border border-white/10 bg-graphite-950 p-3">
                <div className="text-[10px] uppercase tracking-widest text-ink-secondary flex items-center gap-1.5">
                  <ClipboardText weight="duotone" className="h-3 w-3" /> Public URL
                </div>
                <div className="mt-1 font-mono text-xs text-ink-primary break-all">{publicUrlFor(forms.find(x => x.form_id === editing)?.public_slug || "")}</div>
                <p className="mt-1 text-[11px] text-ink-secondary/60">Share this URL — no login required for submitters.</p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button data-testid="form-dialog-copy-url" onClick={() => copyUrl(forms.find(x => x.form_id === editing)?.public_slug || "")}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-[11px] text-ink-secondary hover:text-ink-primary hover:bg-white/5">
                    <Copy weight="bold" className="h-3 w-3" /> Copy URL
                  </button>
                  <button data-testid="form-dialog-copy-embed" onClick={() => copyEmbed(forms.find(x => x.form_id === editing)?.public_slug || "")}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-[11px] text-ink-secondary hover:text-ink-primary hover:bg-white/5">
                    <Code weight="bold" className="h-3 w-3" /> Copy embed
                  </button>
                </div>
              </div>
            )}

            {editing && (
              <div className="rounded-md border border-white/10 bg-graphite-950 p-3" data-testid="form-embed-section">
                <div className="text-[10px] uppercase tracking-widest text-ink-secondary flex items-center gap-1.5">
                  <Code weight="duotone" className="h-3 w-3" /> Embed snippet
                </div>
                <p className="mt-1 text-[11px] text-ink-secondary/60">Paste this HTML into any site (WordPress, Wix, Webflow, custom) to host the form inline.</p>
                <pre data-testid="form-embed-snippet" className="mt-2 rounded-md bg-graphite-900 border border-white/10 p-3 text-[11px] font-mono text-ink-primary overflow-x-auto whitespace-pre-wrap break-all">{embedSnippetFor(forms.find(x => x.form_id === editing)?.public_slug || "")}</pre>
              </div>
            )}
            </TabsContent>
            <TabsContent value="theme" className="mt-4">
              <LandingPageTheme
                value={form.theme}
                onChange={(next) => setForm(prev => ({ ...prev, theme: next }))}
              />
              <p className="mt-4 text-[11px] text-ink-secondary/70">
                The theme applies to both the standalone public form page (<code>/f/&hellip;</code>) and to
                the form when embedded on a Landing Page (transparent background so it inherits the
                page's own theme).
              </p>
            </TabsContent>
          </Tabs>
          <DialogFooter className="gap-2 mt-4">
            <button data-testid="form-form-cancel" onClick={() => { if (!saving) { setDialogOpen(false); setForm(emptyForm); setEditing(null); } }} disabled={saving}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">Cancel</button>
            <button data-testid="form-form-save" onClick={save} disabled={saving || !form.name.trim()}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save changes" : "Create form"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={v => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="form-delete-dialog" className="bg-graphite-900 border-white/10 text-ink-primary">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove form?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary">
              {confirmDelete?.name} will be soft-deleted and its public URL will stop working immediately.
              Existing leads from this form are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="form-delete-cancel" className="bg-graphite-800 border-white/10 text-ink-primary hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="form-delete-confirm" onClick={doDelete} className="bg-red-400/90 text-graphite-950 hover:bg-red-300">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
